// SAD-857: the program glossary for every page of a program. ProgramLayout
// mounts one provider: it reads the glossary once, builds the one matcher, and
// hosts the one glossary card every <GlossaryText> opens. Opening the card
// re-renders only the card (its state lives below the context), never the
// text on the page.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraInlineBanner, SpecraPopover, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentViewerDrawer } from '../../../components/ProposalDashboard/DocumentViewer/DocumentViewerDrawer';
import { useProgramDocuments } from '../Docs/hooks';
import { GlossaryContext } from './GlossaryText';
import { useGlossaryModel } from './hooks';
export function GlossaryProvider({ programId, projectId, children }) {
    const model = useGlossaryModel(programId);
    const opener = useRef(null);
    const open = useCallback((key, anchor)=>opener.current?.(key, anchor), []);
    const value = useMemo(()=>({
            programId,
            entries: model.entries,
            matcher: model.matcher,
            isLoading: model.isLoading,
            error: model.error,
            sectionsRead: model.sectionsRead,
            open
        }), [
        programId,
        model.entries,
        model.matcher,
        model.isLoading,
        model.error,
        model.sectionsRead,
        open
    ]);
    return /*#__PURE__*/ _jsxs(GlossaryContext.Provider, {
        value: value,
        children: [
            children,
            /*#__PURE__*/ _jsx(GlossaryCardHost, {
                programId: programId,
                projectId: projectId,
                matcher: model.matcher,
                opener: opener
            })
        ]
    });
}
function GlossaryCardHost({ programId, projectId, matcher, opener }) {
    const [state, setState] = useState(null);
    const [viewing, setViewing] = useState(null);
    useEffect(()=>{
        opener.current = (key, anchor)=>setState({
                key,
                anchor
            });
        return ()=>{
            opener.current = null;
        };
    }, [
        opener
    ]);
    const entry = state ? matcher?.entries.get(state.key) : undefined;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraPopover, {
                open: !!state && !!entry,
                anchor: state?.anchor ?? null,
                onOpenChange: (o)=>{
                    if (!o) setState(null);
                },
                "aria-label": entry ? `${entry.term}: glossary` : 'Glossary',
                "data-testid": Testids.glossary.popover,
                children: entry ? /*#__PURE__*/ _jsx(GlossaryCard, {
                    programId: programId,
                    entry: entry,
                    canOpen: !!projectId,
                    onOpenPage: (t)=>{
                        setState(null);
                        setViewing(t);
                    }
                }) : null
            }),
            viewing && projectId ? /*#__PURE__*/ _jsx(GlossaryDocumentViewer, {
                programId: programId,
                projectId: projectId,
                target: viewing,
                onClose: ()=>setViewing(null)
            }) : null
        ]
    });
}
/** The program document a glossary ref names, by its program_documents id. */ export function useGlossaryDocuments(programId) {
    const docs = useProgramDocuments(programId);
    return useMemo(()=>{
        const m = new Map();
        for (const d of docs.data?.items ?? []){
            m.set(d.id, {
                title: d.title || d.fileName,
                fileId: d.fileId
            });
        }
        return m;
    }, [
        docs.data
    ]);
}
/** The ref a meaning shows: the first one quoting its definition. */ export function primaryRef(sense) {
    return sense.refs.find((r)=>r.quote) ?? sense.refs[0];
}
const QUOTE_LIMIT = 240;
function clip(quote) {
    return quote.length > QUOTE_LIMIT ? `${quote.slice(0, QUOTE_LIMIT).trimEnd()}…` : quote;
}
function GlossaryCard({ programId, entry, canOpen, onOpenPage }) {
    const docs = useGlossaryDocuments(programId);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-[var(--space-2-5)]",
        "data-term": entry.key,
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                size: "body-ui",
                className: "font-semibold",
                children: entry.term
            }),
            entry.conflict ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                variant: "framed",
                title: "The documents define this two ways",
                "data-testid": Testids.glossary.popoverConflict,
                children: "Each meaning quotes where it is printed."
            }) : null,
            entry.senses.map((s)=>{
                const ref = primaryRef(s);
                const doc = ref ? docs.get(ref.documentId) : undefined;
                return /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-[var(--space-1)]",
                    "data-testid": Testids.glossary.popoverSense,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "div",
                            size: "body-ui",
                            children: s.expansion || 'No meaning printed'
                        }),
                        s.inferred ? /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            size: "meta",
                            tone: "muted",
                            children: "Not defined in the documents; read from how they use it."
                        }) : null,
                        ref?.quote ? /*#__PURE__*/ _jsxs("blockquote", {
                            className: "border-l-2 border-border pl-[var(--space-2)] text-[length:var(--fs-ui-meta)] italic text-muted-foreground",
                            children: [
                                "“",
                                clip(ref.quote),
                                "”"
                            ]
                        }) : null,
                        ref ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between gap-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    className: "min-w-0 truncate",
                                    children: [
                                        doc?.title ?? 'Source document',
                                        ref.page > 0 ? `, p. ${ref.page}` : ''
                                    ]
                                }),
                                canOpen && doc?.fileId ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    "data-testid": Testids.glossary.openPage,
                                    "data-page": ref.page || 1,
                                    onClick: ()=>onOpenPage({
                                            documentId: ref.documentId,
                                            page: ref.page || 1
                                        }),
                                    children: ref.page > 0 ? `Open page ${ref.page}` : 'Open document'
                                }) : null
                            ]
                        }) : null
                    ]
                }, s.ids[0]);
            })
        ]
    });
}
export function GlossaryDocumentViewer({ programId, projectId, target, onClose }) {
    const docs = useGlossaryDocuments(programId);
    const doc = docs.get(target.documentId);
    if (!doc?.fileId) return null;
    return /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
        isOpen: true,
        onClose: onClose,
        projectId: projectId,
        document: {
            documentId: doc.fileId,
            filename: doc.title
        },
        initialPage: target.page
    });
}
